/**
 * Auth Service — Donor Portal
 *
 * Pure TypeScript functions for credential-based login and logout.
 * No Vue dependencies — this service is store- and framework-agnostic.
 *
 * All API calls go through the singleton API client so that request
 * interceptors (auth headers, tenant hostname) are applied consistently.
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type { CognitoLoginResponse } from '@tms-phoenix/tms-types';

// ── Types ────────────────────────────────────────────────────────────────────

export type AuthLoginCredentials = {
  email: string;
  password: string;
};

/**
 * Response shape from POST /onboarding/api/v1/auth/login (after envelope unwrap).
 *
 * Two variants:
 *  - `type: 'authenticated'` → tokens + user (CognitoTokenResponse)
 *  - `type: 'challenge'`     → Cognito challenge (e.g. NEW_PASSWORD_REQUIRED)
 *
 * Uses the shared CognitoLoginResponse from @tms-phoenix/tms-types as the
 * single source of truth so this service stays in sync with the backend schema.
 */
export type AuthLoginResponse = CognitoLoginResponse;

// Re-export the authenticated branch type so the store can narrow without
// importing directly from @tms-phoenix/tms-types.
export type { CognitoLoginResponse };

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * POST /service/api/v1/auth/login
 *
 * Exchanges email + password for access/refresh tokens and minimal user info.
 */
export async function loginWithCredentials(
  credentials: AuthLoginCredentials,
): Promise<AuthLoginResponse> {
  const api = getApiClient();
  const response = await api.post<AuthLoginResponse>(
    `${API_ROUTES.ONBOARDING}/auth/login`,
    { ...credentials },
  );
  return response.data;
}

/**
 * POST /service/api/v1/auth/logout
 *
 * Invalidates the current session on the server.
 * Errors are swallowed — clearing local state always proceeds.
 *
 * PHX-1180: always sends the request, even without an in-memory access
 * token — the backend falls back to the httpOnly cookie to clear the
 * server-side session, so a post-reload logout (accessToken === null) must
 * still reach the server rather than being skipped client-side.
 */
export async function logoutSession(
  accessToken: string | null,
  timeoutMs?: number,
): Promise<void> {
  try {
    const api = getApiClient();
    await api.post(
      `${API_ROUTES.ONBOARDING}/auth/logout`,
      accessToken ? { access_token: accessToken } : {},
      // Only pass a config object when a timeout was asked for.
      ...(timeoutMs === undefined ? [] : [{ timeout: timeoutMs }]),
    );
  } catch {
    // Non-fatal: local auth state is cleared by the caller regardless
  }
}
